import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { DataGridProProps, GridPaginationModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { TSearchParams } from 'src/@types';
import { IClient } from 'src/@types/apiResponseTypes';
import { clientApi } from 'src/api';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter, CGridHeader } from 'src/components/custom-grid-data-components';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol from 'src/hooks/useGetGridCol';
import { DEFAULT_PAGE_SIZE } from 'src/static/constants';

interface ClientTableProps extends Omit<DataGridProProps, 'columns'> {
  onClickAdd?: () => void;
}

export interface ClientTableRef {
  getClients: () => void;
}

const ClientTable = forwardRef<ClientTableRef, ClientTableProps>(
  ({ onClickAdd, ...props }, ref) => {
    const { callApiWithLoading } = useCallApiWithLoading();
    const apiRef = useGridApiRef();
    const { getClientManagementGridCol } = useGetGridCol();
    const columns = getClientManagementGridCol();
    // clients
    const [clients, setClients] = useState<IClient[]>([]);
    // grid row count
    const [rowCount, setRowCount] = useState<number>(0);
    // search params
    const [searchParams, setSearchParams] = useState<TSearchParams>({
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      text: '',
    });

    // searchParams 변경 시 목록 조회
    useEffect(() => {
      getClients();
    }, [searchParams.page, searchParams.size, searchParams.text]);

    // 목록 조회
    const getClients = async () => {
      try {
        await callApiWithLoading(async () => {
          const result = await clientApi.getClients({
            text: searchParams.text,
            page: searchParams.page + 1,
            size: searchParams.size,
          });

          if (result) {
            setClients(result.data);
            if (result.pagination) {
              setRowCount(result.pagination.totalElements);
              setSearchParams({
                ...searchParams,
                page: result.pagination.page - 1,
                size: result.pagination.size,
              });
            }
            return;
          }

          throw new Error('고객사 목록 조회에 실패했습니다.');
        });
      } catch (error) {
        console.error(error);
      }
    };

    // 검색 시
    const handleSearch = (text: string) => {
      setSearchParams({
        ...searchParams,
        page: 0,
        text,
      });
    };

    // 페이지 모델 변경 시
    const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
      setSearchParams({
        ...searchParams,
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
    };

    useImperativeHandle(ref, () => ({
      getClients: getClients,
    }));

    return (
      <CDataGridPro
        apiRef={apiRef}
        columns={columns}
        rows={clients.map((client) => ({
          id: client.customerSid,
          ...client,
        }))}
        rowCount={rowCount}
        paginationModel={{
          page: searchParams.page,
          pageSize: searchParams.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        checkboxSelection={false}
        slots={{
          footer: CGridFooter,
          toolbar: CGridHeader,
        }}
        slotProps={{
          toolbar: {
            onClickAdd: onClickAdd,
            onSearch: handleSearch,
          },
        }}
        {...props}
      />
    );
  },
);
export default ClientTable;
